// Geometry + view helpers for the NetLab editor (all positions are [lon, lat]).

export const keyOf = (table, id) => `${table}:${id}`;

// Tag every drawable object with table/kind/key so the map, selection and
// filters can treat buses, branches and single-bus elements uniformly.
export function normalizeView(view) {
  const tag = (arr, table, kind) => (arr || []).map(o => ({
    ...o, table: o.table || table, kind: o.kind || kind, key: keyOf(o.table || table, o.id),
  }));
  const v = {
    ...view,
    buses: tag(view.buses, 'bus', 'bus'),
    lines: tag(view.lines, 'line', 'line'),
    trafos: tag(view.trafos, 'trafo', 'trafo'),
    switches: tag(view.switches, 'switch', 'switch'),
    elements: tag(view.elements),
  };
  const byKey = new Map();
  for (const arr of [v.buses, v.lines, v.trafos, v.switches, v.elements]) {
    for (const o of arr) byKey.set(o.key, o);
  }
  v.byKey = byKey;
  v.busById = new Map(v.buses.map(b => [b.id, b]));
  return v;
}

// Metres between two lon/lat points (equirectangular: fine at grid scale).
export function metres(a, b) {
  const k = Math.cos(((a[1] + b[1]) / 2) * Math.PI / 180);
  const dx = (b[0] - a[0]) * 111320 * k;
  const dy = (b[1] - a[1]) * 110540;
  return Math.hypot(dx, dy);
}

export function nearestBus(view, pos, predicate = () => true) {
  let best = null, bestD = Infinity;
  for (const b of view.buses) {
    if (!predicate(b)) continue;
    const d = metres(pos, b.pos);
    if (d < bestD) { bestD = d; best = b; }
  }
  return best ? { bus: best, distance: bestD } : null;
}

function segDistance(p, a, b) {
  const k = Math.cos(p[1] * Math.PI / 180);
  const P = [p[0] * k, p[1]], A = [a[0] * k, a[1]], B = [b[0] * k, b[1]];
  const dx = B[0] - A[0], dy = B[1] - A[1];
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((P[0] - A[0]) * dx + (P[1] - A[1]) * dy) / len2)) : 0;
  const q = [(A[0] + t * dx) / k, A[1] + t * dy];
  return metres(p, q);
}

export function nearestLine(view, pos) {
  let best = null, bestD = Infinity;
  for (const l of view.lines) {
    for (let i = 1; i < l.path.length; i++) {
      const d = segDistance(pos, l.path[i - 1], l.path[i]);
      if (d < bestD) { bestD = d; best = l; }
    }
  }
  return best ? { line: best, distance: bestD } : null;
}

export function fmtDistance(m) {
  if (m == null) return '';
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`;
}

// Web-Mercator fit of a [minLon, minLat, maxLon, maxLat] box into width x height px.
export function fitBounds(bounds, width, height, padding = 60) {
  if (!bounds) return { longitude: 14.5, latitude: 47.5, zoom: 7 };
  const [minLon, minLat, maxLon, maxLat] = bounds;
  const merc = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 180) / 2));
  const w = Math.max(1, width - 2 * padding), h = Math.max(1, height - 2 * padding);
  const lonSpan = Math.max(maxLon - minLon, 1e-5) / 360;
  const latSpan = Math.max(merc(maxLat) - merc(minLat), 1e-7) / (2 * Math.PI);
  const zoom = Math.min(Math.log2(w / 512 / lonSpan), Math.log2(h / 512 / latSpan), 19);
  const midMerc = (merc(maxLat) + merc(minLat)) / 2;
  const latitude = (2 * Math.atan(Math.exp(midMerc)) - Math.PI / 2) * 180 / Math.PI;
  return { longitude: (minLon + maxLon) / 2, latitude, zoom };
}

